/**
 * Dark mode is one token set, not a second stylesheet. Everything this module
 * does is set `data-theme` on the root element; `styles.css` swaps the custom
 * properties underneath. No component reads the theme, so nothing re-renders.
 */

export type Theme = 'light' | 'dark';

/**
 * Duplicated, deliberately, by the boot script `public/theme-init.js`. That
 * script has to run before the stylesheet paints and cannot import a module,
 * so the two copies must be changed together; `themeInit.test.ts` holds them to
 * it.
 */
export const STORAGE_KEY = 'driftwatch-theme';
const ATTRIBUTE = 'data-theme';

/**
 * Light, for everybody, until they say otherwise.
 *
 * This used to be `prefers-color-scheme`, which is the polite default and the
 * wrong one here. The landing page is the first thing a stranger sees, and what
 * they see is decided by a setting on their laptop rather than by us — the same
 * page is a white document to one visitor and a dark one to the next, and only
 * one of those was ever looked at while it was being designed. A product whose
 * front door changes appearance depending on the machine has no front door.
 *
 * The switch is still there and still remembered. What is gone is the machine
 * getting a vote nobody asked it for.
 */
const DEFAULT_THEME: Theme = 'light';

type Listener = (theme: Theme) => void;

const listeners = new Set<Listener>();

const isTheme = (value: unknown): value is Theme => value === 'light' || value === 'dark';

/**
 * Safari with cookies blocked throws on `localStorage` rather than returning
 * null. A colour preference is never worth taking the console down for, so
 * every access degrades to "no stored choice".
 */
const readStoredTheme = (): Theme | null => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    return null;
  }
};

const writeStoredTheme = (theme: Theme): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // The attribute is already applied; only persistence across reloads is lost.
  }
};

const applyTheme = (theme: Theme): void => {
  document.documentElement.setAttribute(ATTRIBUTE, theme);
};

const notify = (theme: Theme): void => {
  for (const listener of listeners) listener(theme);
};

/** A choice made here, or light. Nothing else gets a say. */
export const getTheme = (): Theme => readStoredTheme() ?? DEFAULT_THEME;

/** For a switch that has to say what it will do rather than what is on. */
export const otherTheme = (theme: Theme): Theme => (theme === 'dark' ? 'light' : 'dark');

export const setTheme = (theme: Theme): void => {
  writeStoredTheme(theme);
  applyTheme(theme);
  notify(theme);
};

export const subscribeTheme = (fn: Listener): (() => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

/**
 * Call before `createRoot`. `index.html` has already applied the same answer
 * from its inline script — it has to, because a module script is deferred and
 * would repaint after the first frame — and this reconciles with it.
 *
 * There is no longer a `prefers-color-scheme` listener. A machine that flips to
 * dark at sunset used to take the console with it mid-session, which is a
 * change of appearance nobody asked for while they were reading a report.
 */
export const initTheme = (): void => {
  applyTheme(getTheme());
};
